import { computed, defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";

import {
    Gender,
    HospiceHispanicEthnicities,
    HospiceHispanicEthnicitiesEnum,
    HospiceRaces,
    ICustomEnumValue,
    MaritalStatuses,
    PhoneTypeEnum,
    RacesPalliative,
    Veterans
} from "../../../../../../enums/enums";
import { IBranchResponse } from "../../../../../../resources-vue/vue-interfaces/i-branch";
import { BranchesService } from "../../../../../../services/branches-service";
import { useHospiceConvertToPending } from "../../../store/useHospiceConvertToPending";
import { Address } from "../../../../../../resources-vue/vue-models/address";
import { Interpreter } from "../../../../../../enums/HosConvertToPendingInterpreter";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { ProvidersService } from "../../../../../../services/providers-service";
import { IGetPatientTag } from "../../../../../../resources-vue/vue-interfaces/i-providers-service";
import { IPhoneNumber } from "../../../../../../resources-vue/vue-interfaces/i-phone-number";
import { ILanguagesResponse, LookupService } from "../../../../../../services/lookup-service";
import { PatientService } from "../../../../../../services/patient.service";
import { ToastrService } from "../../../../../../services/toastr.service";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import MultiSelectDropdown from "../../../../../../resources-vue/vue-custom-elements/MultiSelectDropdown/MultiSelectDropdown.vue";
import PhoneNumber from "../../../../../../resources-vue/vue-custom-elements/PhoneNumber/PhoneNumber.vue";
import TagsInput from "../../../../../../resources-vue/vue-custom-elements/TagsInput/TagsInput.vue";
import AddressForm from "../../AddressForm/AddressForm.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import ValidateAddress from "../../../../../../resources-vue/vue-dialogs/ValidateAddress/ValidateAddress.vue";

export default defineComponent({
    inject: ["v"],
    components: {
        FormSection,
        DatePicker,
        MultiSelectDropdown,
        PhoneNumber,
        TagsInput,
        AddressForm,
        TypeaheadInput
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    computed: {
        ...mapState(useHospiceConvertToPending, ["accountId", "patientDetails", "application"]),
        veteranOptions() {
            return this.Veterans?.map((option) => ({
                name: option.name,
                value: option.value
            }));
        },
        genderOptions() {
            return this.Gender.filter((gender) => {
                return gender.value !== 3;
            });
        },
        primaryNumber: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.primaryNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.number;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[this.primaryNumberIndex(this.patientDetails.phoneNumbers)].number =
                    value;
            }
        },
        primaryExtension: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.primaryNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.extension;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[this.primaryNumberIndex(this.patientDetails.phoneNumbers)].extension =
                    value;
            }
        },
        primaryType: {
            get() {
                const value =
                    this.patientDetails.phoneNumbers?.[this.primaryNumberIndex(this.patientDetails.phoneNumbers)]?.type;
                return value;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[this.primaryNumberIndex(this.patientDetails.phoneNumbers)].type =
                    value;
            }
        },
        primaryCountry: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.primaryNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.country;
            },
            set(value: any) {
                return (this.patientDetails.phoneNumbers[
                    this.primaryNumberIndex(this.patientDetails.phoneNumbers)
                ].country = value);
            }
        },
        alternateNumber: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.number;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)].number =
                    value;
            }
        },
        alternateExtension: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.extension;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.phoneNumbers)
                ].extension = value;
            }
        },
        alternateType: {
            get() {
                const value =
                    this.patientDetails.phoneNumbers?.[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)]
                        ?.type;
                return value;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)].type =
                    value;
            }
        },
        alternateCountry: {
            get() {
                return this.patientDetails.phoneNumbers?.[this.alternativeNumberIndex(this.patientDetails.phoneNumbers)]
                    ?.country;
            },
            set(value: any) {
                this.patientDetails.phoneNumbers[
                    this.alternativeNumberIndex(this.patientDetails.phoneNumbers)
                ].country = value;
            }
        },
        isUnitedStates() {
            return this.patientDetails.primaryAddress.country === "840";
        }
    },

    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _patientService: null,
            dialogRef: null,
            Veterans: Array.from(Veterans.enumWithDescriptions.values()),
            MaritalStatuses: Array.from(MaritalStatuses.enumWithDescriptions.values()),
            RacesEthnicity: Array.from(RacesPalliative.enumWithDescriptions.values()),
            RacesHospice: Array.from(HospiceRaces.enumWithDescriptions.values()),
            EthnicityHospice: Array.from(HospiceHispanicEthnicities.enumWithDescriptions.values()),
            phoneNumberTypeOptionsConfig: [PhoneTypeEnum.Home, PhoneTypeEnum.Mobile, PhoneTypeEnum.Facility],
            Gender: Array.from(Gender.enumWithDescriptions.values()),
            branches: null as IBranchResponse[],
            _branchesService: null,
            _lookupService: null,
            _visitAddressCache: null,
            _visitAddressFacilityCache: null,
            _mailingAddressCache: null,
            _mailingAddressFacilityCache: null,
            _providersService: null,
            selectedLanguage: null,
            interpreters: Array.from(Interpreter.enumWithDescriptions.values()),
            isVeteranSelected: false,
            selectedEthnicity: [],
            selectedAdditionalLanguages: [],
            patientTags: [],
            selectedTags: [],
            selectedRacesHospice: [],
            selectedEthnicityHospice: [],
            isAddressValidationInProgress: false,
            v$: this.v,
            lastPatientMRN: "",
            formatDateInput
        };
    },

    created() {
        this._branchesService = Container.instance.get(BranchesService);
        this._providersService = Container.instance.get(ProvidersService);
        this._lookupService = Container.instance.get(LookupService);
        this._patientService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this.selectedRacesHospice = this.RacesHospice.filter((race) => {
            return this.patientDetails.race.some((item) => {
                return race.value == item;
            });
        }) as ICustomEnumValue<number>[];
        this.selectedEthnicityHospice = this.EthnicityHospice.filter((ethnicity) => {
            return this.patientDetails.hispanicEthnicityValues.some((item) => {
                return ethnicity.value == item;
            });
        }) as ICustomEnumValue<number>[];
    },
    mounted() {
        this.getLastMrn();
        this.getActivePatientTags();
        this.getBranches();
        this.initFields();
    },

    watch: {
        selectedEthnicity() {
            if (!!this.selectedEthnicity && !!this.patientDetails) {
                this.patientDetails.ethnicity = this.selectedEthnicity.map((ethnicity) => Number(ethnicity.value));
            }
        },
        selectedTags() {
            if (!!this.selectedTags && !!this.patientDetails) {
                this.patientDetails.tagIds = this.selectedTags.map((tag: any) => tag.value);
            }
        },

        selectedAdditionalLanguages() {
            if (!!this.selectedAdditionalLanguages && !!this.patientDetails) {
                this.patientDetails.languages = this.selectedAdditionalLanguages.map((language: any) => language.value);
            }
        }
    },

    methods: {
        async getLastMrn() {
            const payload = {
                application: Number(this.application),
                accountId: this.accountId
            };
            try {
                const response = await this._patientService.getLastPatientMrn(payload);
                this.lastPatientMRN = response.LastUsedMRN;
            } catch (err) {
                console.error(err);
            }
        },

        primaryNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (!!phoneNumbers) {
                return phoneNumbers.findIndex((e) => e.isPrimary);
            }
            return 0;
        },
        alternativeNumberIndex(phoneNumbers: IPhoneNumber[]) {
            if (!!phoneNumbers) {
                return phoneNumbers.findIndex((e) => !e.isPrimary);
            }
            return 0;
        },
        async getActivePatientTags() {
            const patientTagsQuery = {
                providerIds: [this.patientDetails.providerId],
                isActive: true,
                application: this.application,
                accountId: this.accountId
            };
            let patientTags = await this._providersService.getActivePatientTags(patientTagsQuery);
            this.patientTags = patientTags.map((tag: IGetPatientTag) => ({
                name: tag.name,
                value: tag.id
            }));
            this.selectedTags = this.patientDetails.tags?.map((tag: any) => ({ name: tag.name, value: tag.id }));
        },

        async initFields() {
            if (!!this.patientDetails) {
                if (this.patientDetails.primaryLanguage?.length) {
                    const payload = {
                        application: this.application,
                        agencyId: this.accountId,
                        codes: this.patientDetails.primaryLanguage
                    };
                    const languageDetails = await this._lookupService.getLanguageName(payload);
                    this.selectedLanguage = { name: languageDetails[0].name, value: languageDetails[0].code };
                }
                if (this.patientDetails.languages?.length) {
                    const payload = {
                        application: this.application,
                        agencyId: this.accountId,
                        codes: this.patientDetails.languages
                    };
                    const languageDetails = await this._lookupService.getLanguageName(payload);
                    this.selectedAdditionalLanguages = languageDetails.map((language: any) => {
                        return { name: language.name, value: language.code };
                    });
                }

                this._mailingAddressCache = new Address(this.patientDetails.mailingAddress);
                this._mailingAddressFacilityCache = this.patientDetails.mailingAddressFacilityTypeId;

                this._visitAddressCache = new Address(this.patientDetails.visitAddress);
                this._visitAddressFacilityCache = this.patientDetails.visitAddressFacilityTypeId;

                this.selectedEthnicity = this.RacesEthnicity.filter((ethnicity) => {
                    return this.patientDetails.ethnicity?.some((item: any) => {
                        return ethnicity.value == item;
                    });
                }) as any;
                this.selectedEthnicityHospice = this.EthnicityHospice.filter((ethnicity) => {
                    return this.patientDetails.hispanicEthnicityValues.some((item) => {
                        return ethnicity.value == item;
                    });
                }) as ICustomEnumValue<number>[];

                if (this.patientDetails.veteran?.length > 0) {
                    this.isVeteranSelected = true;
                }
                if (this.primaryNumberIndex(this.patientDetails.phoneNumbers) < 0) {
                    this.patientDetails.phoneNumbers = this.patientDetails.phoneNumbers.concat({
                        id: "",
                        number: "",
                        extension: "",
                        type: 1,
                        country: null,
                        isPrimary: true
                    });
                }
                if (this.alternativeNumberIndex(this.patientDetails.phoneNumbers) < 0) {
                    this.patientDetails.phoneNumbers = this.patientDetails.phoneNumbers.concat({
                        id: "",
                        number: "",
                        extension: "",
                        type: 1,
                        country: null,
                        isPrimary: false
                    });
                }
            }
        },

        async getBranches() {
            const branches = await this._branchesService.getAllBranches();
            this.branches = branches.filter((branch: IBranchResponse) => branch.accountId == this.accountId);
        },
        handleSetVisitAddressSameAsPrimary() {
            if (this.patientDetails.isVisitAddressSameAsPrimary === true) {
                this.patientDetails.visitAddress = new Address(this.patientDetails.primaryAddress);
                this.patientDetails.visitAddressFacilityTypeId = this.patientDetails.primaryAddressFacilityTypeId;
            } else {
                this.patientDetails.isVisitAddressSameAsPrimary = false;
                if (this._visitAddressCache) {
                    this.patientDetails.visitAddress = new Address(this._visitAddressCache);
                    this.patientDetails.visitAddressFacilityTypeId = this._visitAddressFacilityCache;
                } else {
                    this.patientDetails.visitAddress = new Address();
                    this.patientDetails.visitAddressFacilityTypeId = null;
                }
            }
        },
        handleSetMailingAddressSameAsPrimary() {
            if (this.patientDetails.isMailingAddressSameAsPrimary === true) {
                this.patientDetails.mailingAddress = new Address(this.patientDetails.primaryAddress);
                this.patientDetails.mailingAddressFacilityTypeId = this.patientDetails.primaryAddressFacilityTypeId;
            } else {
                this.patientDetails.isMailingAddressSameAsPrimary = false;
                if (this._mailingAddressCache) {
                    this.patientDetails.mailingAddress = new Address(this._mailingAddressCache);
                    this.patientDetails.mailingAddressFacilityTypeId = this._mailingAddressFacilityCache;
                } else {
                    this.patientDetails.mailingAddress = new Address();
                    this.patientDetails.mailingAddressFacilityTypeId = null;
                }
            }
        },
        async getLanguageList(filter: any) {
            const payload = {
                term: filter,
                application: Number(this.application)
            };

            const response = await this._lookupService.getLanguages(payload);
            return response.data.map((language: ILanguagesResponse) => ({
                name: language.name,
                value: language.code
            }));
        },

        updateLanguage(newValue: any) {
            this.selectedLanguage = newValue;
            this.patientDetails.primaryLanguage = newValue.value;
        },

        updateAdditionalLanguage(newValue: any) {
            this.selectedAdditionalLanguages = newValue;
        },

        updateSelectedTags(newValue: any) {
            this.selectedTags = newValue;
        },
        updateSelectedHospiceRaces(value: ICustomEnumValue<number>[]) {
            this.selectedRacesHospice = value;
            this.patientDetails.race = this.selectedRacesHospice.map((race) => Number(race.value));
        },

        updateSelectedHospiceEthnictiy(value) {
            const values = value.map((ethnicity) => Number(ethnicity.value));
            let isError = false;
            if (
                values.includes(HospiceHispanicEthnicitiesEnum.NoNotOfHispanicLatinoOrSpanishOrigin) &&
                (values.includes(HospiceHispanicEthnicitiesEnum.YesMexicanAmericanChicano) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesPuertoRican) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesCuban) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.YesAnotherHispanicLatinoOrSpanishOrigin) ||
                    values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond))
            ) {
                isError = true;
            } else if (
                values.includes(HospiceHispanicEthnicitiesEnum.PatientUnableToRespond) &&
                values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond)
            ) {
                isError = true;
            } else if (values.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond) && values.length > 1) {
                isError = true;
            }
            if (isError) {
                this._toastrService.error({
                    title: "Error",
                    message:
                        "Invalid Combination for HOPE Ethnicity: Y must be selected alone; A cannot be combined with B-E or Y; B-E cannot be combined with Y; X cannot be combined with Y."
                });
            }
            this.selectedEthnicityHospice = value;
            this.patientDetails.hispanicEthnicityValues = this.selectedEthnicityHospice.map((ethnicity) =>
                Number(ethnicity.value)
            );
        },

        updateVeteranSelected(value: any) {
            if (this.patientDetails.veteran.includes(value)) {
                const index = this.patientDetails.veteran.indexOf(value);
                this.patientDetails.veteran.splice(index, 1);
                return;
            }
            this.patientDetails.veteran.push(value);
        },
        getDate(date: any, name: string) {
            if (name === "DOB") {
                this.patientDetails.dateOfBirth = date;
            }
        },
        updateEthnicity(newValue: any) {
            this.selectedEthnicity = newValue;
        },

        resetSameAsPrimaryAddress() {
            if (this.patientDetails.isVisitAddressSameAsPrimary) {
                this.visitAddrSameAsPrimaryAddrChanged(true);
            }
            if (this.patientDetails.isMailingAddressSameAsPrimary) {
                this.mailingAddrSameAsPrimaryAddrChanged(true);
            }
        },
        mailingAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.patientDetails) {
                if (newValue === true) {
                    this.patientDetails.isMailingAddressSameAsPrimary = true;
                    this.patientDetails.mailingAddress = new Address(this.patientDetails.primaryAddress);
                    this.patientDetails.mailingAddressFacilityTypeId = this.patientDetails.primaryAddressFacilityTypeId;
                } else {
                    this.patientDetails.isMailingAddressSameAsPrimary = false;
                    if (this._mailingAddressCache) {
                        this.patientDetails.mailingAddress = new Address(this._mailingAddressCache);
                        this.patientDetails.mailingAddressFacilityTypeId = this._mailingAddressFacilityCache;
                    } else {
                        this.patientDetails.mailingAddress = new Address();
                        this.patientDetails.mailingAddressFacilityTypeId = null;
                    }
                }
            }
        },
        visitAddrSameAsPrimaryAddrChanged(newValue: boolean) {
            if (!!this.patientDetails) {
                if (newValue === true) {
                    this.patientDetails.isVisitAddressSameAsPrimary = true;
                    this.patientDetails.visitAddress = new Address(this.patientDetails.primaryAddress);
                    this.patientDetails.visitAddressFacilityTypeId = this.patientDetails.primaryAddressFacilityTypeId;
                } else {
                    this.patientDetails.isVisitAddressSameAsPrimary = false;
                    if (this._visitAddressCache) {
                        this.patientDetails.visitAddress = new Address(this._visitAddressCache);
                        this.patientDetails.visitAddressFacilityTypeId = this._visitAddressFacilityCache;
                    } else {
                        this.patientDetails.visitAddress = new Address();
                        this.patientDetails.visitAddressFacilityTypeId = null;
                    }
                }
            }
        },
        async validateAddress() {
            let model = {
                originalAddress: {
                    addressLine1: this.patientDetails.primaryAddress.addressLine1,
                    addressLine2: this.patientDetails.primaryAddress.addressLine2,
                    city: this.patientDetails.primaryAddress.city,
                    state: this.patientDetails.primaryAddress.state,
                    zipCode: this.patientDetails.primaryAddress.zipCode,
                    county: this.patientDetails.primaryAddress.county,
                    country: this.patientDetails.primaryAddress.country,
                    latitude: this.patientDetails.primaryAddress.latitude,
                    longitude: this.patientDetails.primaryAddress.longitude
                }
            };
            this.isAddressValidationInProgress = true;
            let updatedAddress = null;
            this.dialogRef = this.$dialog.open(ValidateAddress, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    model
                },
                onClose: (options) => {
                    if (options.data?.success) {
                        updatedAddress = options.data.updatedAddress;
                        this.patientDetails.primaryAddress.addressLine1 = updatedAddress.addressLine1;
                        this.patientDetails.primaryAddress.addressLine2 = updatedAddress.addressLine2;
                        this.patientDetails.primaryAddress.city = updatedAddress.city;
                        this.patientDetails.primaryAddress.state = updatedAddress.state;
                        this.patientDetails.primaryAddress.zipCode = updatedAddress.zipCode;
                        this.patientDetails.primaryAddress.county = updatedAddress.county;
                        this.patientDetails.primaryAddress.latitude = updatedAddress.latitude;
                        this.patientDetails.primaryAddress.longitude = updatedAddress.longitude;
                        this.patientDetails.primaryAddress.isValidated = true;
                        this.patientDetails.primaryAddress.isBypassed = false;
                        if (updatedAddress.country) {
                            this.patientDetails.primaryAddress.country = updatedAddress.country;
                        }
                    } else {
                        this.patientDetails.primaryAddress.isValidated = false;
                        this.patientDetails.primaryAddress.isBypassed = true;
                    }
                }
            });
            this.isAddressValidationInProgress = false;
        }
    }
});
